import React, { useEffect, useMemo, useState } from 'react';
// Live cams for a forecast spot — renders the spot doc's `cams` array
// (seeded defaults in data/forecastSpots.js, admin-editable with no deploy).
// Cam types: 'still' (snapshot JPEG re-fetched on a timer), 'youtube' (live
// stream embed), 'embed' (arbitrary iframe), 'link' (link-out card).
import { setSpotCams } from '../data/forecastSpots';

// How often snapshot cams re-fetch (Surfline stills update every ~3 min).
const STILL_REFRESH_MS = 3 * 60 * 1000;

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const youtubeId = (url) => {
  const m = String(url || '').match(/(?:youtu\.be\/|[?&]v=|\/embed\/|\/live\/)([\w-]{6,})/);
  return m ? m[1] : null;
};

// A snapshot older than this gets a ⚠ stale flag — research showed several
// providers keep serving frames from cams that died months ago.
const STALE_MS = 30 * 60 * 1000;

const fmtClock = (ms) =>
  new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

const fmtAge = (ageMs) => {
  const m = Math.floor(ageMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
};

function CamStill({ cam }) {
  const [tick, setTick] = useState(() => Date.now());
  const [dead, setDead] = useState(false);
  // When the displayed frame was CAPTURED (the CDN's Last-Modified —
  // CORS-safelisted, so it's readable cross-origin). Null until known;
  // falls back to the fetch time if the header can't be read.
  const [takenAt, setTakenAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), STILL_REFRESH_MS);
    // Separate faster tick just to keep the "Xm ago" age honest.
    const ageId = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => {
      clearInterval(id);
      clearInterval(ageId);
    };
  }, []);
  // Cache-buster keyed to the refresh tick so the CDN's latest frame loads.
  const src = `${cam.url}${cam.url.includes('?') ? '&' : '?'}t=${tick}`;
  useEffect(() => {
    let alive = true;
    fetch(src, { method: 'HEAD', cache: 'no-store' })
      .then((r) => {
        if (!alive) return;
        const lm = Date.parse(r.headers.get('last-modified') || '');
        setTakenAt(Number.isFinite(lm) ? lm : Date.now());
      })
      .catch(() => {
        if (alive) setTakenAt((t) => t ?? Date.now());
      });
    return () => {
      alive = false;
    };
  }, [src]);
  if (dead) return null;
  const age = takenAt != null ? Math.max(0, now - takenAt) : null;
  const stale = age != null && age > STALE_MS;
  return (
    <a className="cs-fc-cam" href={cam.link || cam.url} target="_blank" rel="noreferrer" title={`${cam.title || 'Cam'} — opens ${hostOf(cam.link || cam.url)}`}>
      <span className="cs-fc-cam-shot">
        <img src={src} alt={cam.title || 'Surf cam'} loading="lazy" onError={() => setDead(true)} />
        {age != null && (
          <span
            className={`cs-fc-cam-age ${stale ? 'stale' : ''}`}
            title={`Snapshot taken ${new Date(takenAt).toLocaleString()}`}
          >
            {stale ? '⚠ ' : ''}{fmtAge(age)}
          </span>
        )}
      </span>
      <span className="cs-fc-cam-cap">
        <strong>{cam.title || 'Cam'}</strong>
        <em>
          {cam.attribution ? `${cam.attribution} · ` : ''}
          {takenAt != null ? fmtClock(takenAt) : 'snapshot'}
        </em>
      </span>
    </a>
  );
}

function CamFrame({ cam }) {
  const src = cam.type === 'youtube' ? (youtubeId(cam.url) ? `https://www.youtube.com/embed/${youtubeId(cam.url)}` : null) : cam.url;
  if (!src) return null;
  return (
    <span className="cs-fc-cam">
      <span className="cs-fc-cam-frame">
        <iframe src={src} title={cam.title || 'Surf cam'} loading="lazy" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
      </span>
      <span className="cs-fc-cam-cap">
        <strong>{cam.title || 'Live cam'}</strong>
        <em>{cam.attribution ? `${cam.attribution} · ` : ''}live</em>
      </span>
    </span>
  );
}

function CamLink({ cam }) {
  return (
    <a className="cs-fc-cam cs-fc-cam-link" href={cam.url} target="_blank" rel="noreferrer">
      <span className="cs-fc-cam-link-icon">📷</span>
      <span className="cs-fc-cam-cap">
        <strong>{cam.title || 'Live cam'}</strong>
        <em>{cam.attribution || hostOf(cam.url)} ↗</em>
      </span>
    </a>
  );
}

function CamEditor({ spot }) {
  const cams = useMemo(() => (Array.isArray(spot.cams) ? spot.cams : []), [spot.cams]);
  const [type, setType] = useState('still');
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');

  const save = (next) => setSpotCams(spot.id, next).catch(() => {});
  const add = (e) => {
    e.preventDefault();
    const u = url.trim();
    if (!u) return;
    save([...cams, { type, url: u, title: title.trim() }]);
    setUrl('');
    setTitle('');
  };
  return (
    <div className="cs-fc-cams-edit">
      {cams.map((c, i) => (
        <div key={`${c.url}-${i}`} className="cs-fc-cams-edit-row">
          <span>{c.type}</span>
          <em title={c.url}>{c.title || hostOf(c.url)}</em>
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={() => save(cams.filter((_, j) => j !== i))}
            title="Remove cam"
          >
            ✕
          </button>
        </div>
      ))}
      <form className="cs-fc-cams-edit-row" onSubmit={add}>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="still">snapshot</option>
          <option value="youtube">youtube</option>
          <option value="embed">embed</option>
          <option value="link">link</option>
        </select>
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Cam URL…" />
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Label" />
        <button className="cs-btn cs-btn-sm" type="submit" disabled={!url.trim()}>Add</button>
      </form>
    </div>
  );
}

export default function SpotCams({ spot, canEdit }) {
  const cams = Array.isArray(spot.cams) ? spot.cams : [];
  return (
    <div className="cs-fc-cams">
      {cams.length > 0 && (
        <div className="cs-fc-cams-grid">
          {cams.map((c, i) =>
            c.type === 'still' ? (
              <CamStill key={`${c.url}-${i}`} cam={c} />
            ) : c.type === 'youtube' || c.type === 'embed' ? (
              <CamFrame key={`${c.url}-${i}`} cam={c} />
            ) : (
              <CamLink key={`${c.url}-${i}`} cam={c} />
            )
          )}
        </div>
      )}
      {!cams.length && !canEdit && <div className="cs-fc-cams-none">No live cam at this spot yet.</div>}
      {canEdit && <CamEditor spot={spot} />}
    </div>
  );
}
